import { useParams, useNavigate } from "react-router-dom";
import { Form, Button, Input, message, Card, Select, Space, Typography } from "antd";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { fetchExchangeRates, EXCHANGE_RATES_UPDATED_EVENT } from "../../../shared/lib/exchangeRates";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";

const { Text, Title } = Typography;

const IncreasePayment = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [amountSingle, setAmountSingle] = useState("");
  const [amount_main, setAmount_main] = useState("");
  const [amount_secondary, setAmount_secondary] = useState("");
  const [exchangeRateId, setExchangeRateId] = useState(null);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [exchangeRates, setExchangeRates] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const useDualCurrency = Boolean(systemConfig?.secondary_currency_code);

  const selectedRate = useMemo(() => {
    if (!exchangeRateId || !exchangeRates.length) return null;
    return exchangeRates.find((r) => Number(r.id) === Number(exchangeRateId)) || null;
  }, [exchangeRateId, exchangeRates]);

  const appliedMainImpact = useMemo(() => {
    const main = toApiNumber(amount_main, 0);
    const sec = toApiNumber(amount_secondary, 0);
    if (!useDualCurrency) return Math.abs(toApiNumber(amountSingle, 0));
    const secondaryPerMain = selectedRate ? toApiNumber(selectedRate.main_per_secondary, 0) : 0;
    if (!secondaryPerMain) return Math.round(main * 100) / 100;
    return Math.round((main + sec / secondaryPerMain) * 100) / 100;
  }, [amount_main, amount_secondary, amountSingle, selectedRate, useDualCurrency]);

  const loadFx = useCallback(async () => {
    try {
      const cfg = await fetchSystemConfig();
      setSystemConfig(cfg);
      if (!cfg?.secondary_currency_code) {
        setExchangeRates([]);
        setExchangeRateId(null);
        return;
      }
      const rates = await fetchExchangeRates();
      setExchangeRates(rates);
      setExchangeRateId((prev) =>
        prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
      );
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadFx();
  }, [loadFx]);

  useEffect(() => {
    const onFxUpdated = () => loadFx();
    window.addEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
    return () => window.removeEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
  }, [loadFx]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/customers/${id}`);
        if (cancelled) return;
        setCustomerInfo({
          name: res?.data?.name || "",
          total_owed: toApiNumber(res?.data?.total_owed, 0),
        });
      } catch {
        if (!cancelled) setCustomerInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const onSubmit = async () => {
    const secVal = Math.round(toApiNumber(amount_secondary, 0) * 100) / 100;
    if (useDualCurrency && secVal > 0 && !exchangeRateId) {
      message.error(t("increaseDebtRateRequired"));
      return;
    }

    if (useDualCurrency) {
      const m = toApiNumber(amount_main, 0);
      if (m <= 0 && secVal <= 0) {
        message.error(t("increaseDebtEnterAmount"));
        return;
      }
    } else {
      if (toApiNumber(amountSingle, 0) <= 0) {
        message.error(t("increaseDebtEnterAmount"));
        return;
      }
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("increase_debt", "1");
      fd.append("payment_method", "adjustment");
      if (useDualCurrency) {
        fd.append("amount_main", String(Math.round(toApiNumber(amount_main, 0) * 100) / 100));
        fd.append("amount_secondary", String(secVal));
        if (secVal > 0 && exchangeRateId) {
          fd.append("exchange_rate_id", String(exchangeRateId));
        }
      } else {
        fd.append("amount", String(Math.abs(toApiNumber(amountSingle, 0))));
      }
      await apiClient.post(`/customers/pay/${id}`, fd);
      message.success(t("increaseDebtSuccess"));
      setAmountSingle("");
      setAmount_main("");
      setAmount_secondary("");
      navigate("/GetCustomer");
    } catch (error) {
      const msg = error?.response?.data?.message;
      message.error(msg || t("increaseDebtError"));
    } finally {
      setSubmitting(false);
    }
  };

  const mainCode = systemConfig?.currency_code || "IQD";
  const secCode = systemConfig?.secondary_currency_code || "";

  return (
    <div className="list-page" style={{ maxWidth: 720, margin: "0 auto", padding: "24px 16px" }}>
      <Card variant="borderless">
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={3} style={{ marginBottom: 8 }}>
              {t("increaseDebtTitle")}
            </Title>
            <Text type="secondary">{t("increaseDebtSubtitle")}</Text>
          </div>

          {customerInfo && (
            <Card size="small" style={{ background: "#fff7ed", borderColor: "#fed7aa" }}>
              <Text strong>
                {customerInfo.name ? `${customerInfo.name} — ` : ""}
                {t("totalOwed")}: {formatCurrency(customerInfo.total_owed, mainCode)}
              </Text>
            </Card>
          )}

          <Form layout="vertical" onFinish={onSubmit}>
            {useDualCurrency ? (
              <>
                <Form.Item label={t("increaseDebtAmountMain", { code: mainCode })}>
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    value={amount_main}
                    onChange={(e) => setAmount_main(e.target.value)}
                    placeholder="0"
                  />
                </Form.Item>
                <Form.Item label={t("increaseDebtAmountSecondary", { code: secCode })}>
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    value={amount_secondary}
                    onChange={(e) => setAmount_secondary(e.target.value)}
                    placeholder="0"
                  />
                </Form.Item>
                {exchangeRates.length > 0 && (
                  <Form.Item label={t("rateLabel")}>
                    <Select
                      style={{ width: "100%" }}
                      placeholder={t("increaseDebtSelectRate")}
                      value={exchangeRateId}
                      onChange={setExchangeRateId}
                      options={exchangeRates.map((r) => ({
                        value: r.id,
                        label: `${String(r.effective_from || "").slice(0, 10)} — 1 ${mainCode} = ${r.main_per_secondary} ${secCode}`,
                      }))}
                    />
                  </Form.Item>
                )}
                <Card size="small" type="inner" title={t("increaseDebtTotalMainImpact")}>
                  <Text strong>{formatCurrency(appliedMainImpact, mainCode)}</Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t("increaseDebtMainEquivHint")}
                    </Text>
                  </div>
                </Card>
              </>
            ) : (
              <Form.Item label={t("increaseDebtSingleAmount", { code: mainCode })}>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  value={amountSingle}
                  onChange={(e) => setAmountSingle(e.target.value)}
                  placeholder="0"
                />
              </Form.Item>
            )}

            <Form.Item>
              <Space wrap>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  {t("increaseDebtSubmit")}
                </Button>
                <Button
                  onClick={() => {
                    setAmountSingle("");
                    setAmount_main("");
                    setAmount_secondary("");
                  }}
                >
                  {t("reset")}
                </Button>
                <Button onClick={() => navigate("/GetCustomer")}>{t("cancel")}</Button>
              </Space>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default IncreasePayment;
